import React, { useEffect, useRef, useState } from "react";
import { Table, Tag, Input, message, Tabs, ConfigProvider, Spin } from "antd";
import { isNotNullUndefined } from "../helpers/common";
import Pagination from "../helpers/pagination";
import { storage } from "../utils/storage";
import { useNavigate } from "react-router-dom";
import { useFetchReferCommissionAndDiscountHistoryList } from "../services/refercommisionanddiscounthistory.service";
import { IReferCommisionAndDiscount } from "../types/refercommisionanddiscounthistory";

const ReferCommisionAndDiscountHistory: React.FC = () => {
    const { Search } = Input;
    const { Column } = Table;
    const navigate = useNavigate();
    const [loading, setIsLoading] = useState(false);
    const [referCommisionAndDiscountHistoryList, setReferCommisionAndDiscountHistoryList] = useState<IReferCommisionAndDiscount[]>([]);
    const { TabPane } = Tabs;
    const pageNumber = useRef(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const searchValue = useRef<string>("");
    const [activeTab, setActiveTab] = useState('commission');
    const tabStatus = [{ id: 0, name: "Commission" }, { id: 1, name: "Discount" }];

    //#region on-load useEffect operations
    useEffect(() => {
        let userRoleId = parseInt(storage.getUserRoleId() ?? "0");
      if (![1, 4].includes(userRoleId)) {
            navigate("/");
        } else {
            setIsLoading(true);
            fetchReferCommisionAndDiscountHistoryList(activeTab, "", "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, pageSize]);

    //#region pagination operation
    const handlePageChange = (newPage: number) => {
        pageNumber.current = newPage;
        const size = pageSize;
        setPageSize(size);
    };

    const onSearch = (value: any) => {
        fetchReferCommisionAndDiscountHistoryList(activeTab, "", value);
        searchValue.current = value;
    };

    const tabClicked = (key: string) => {
        const tabsName = key === "0" ? "commission" : "discount";
        setActiveTab(tabsName);
        fetchReferCommisionAndDiscountHistoryList(tabsName, "", "");
    };

    const fetchReferCommisionAndDiscountHistoryList = async (tabName: string, userID?: string, searchFilter?: string) => {
        setIsLoading(true);
        setReferCommisionAndDiscountHistoryList([]);
        fetchReferCommissionAndDiscountHistoryMutation.mutate({ tabName: tabName, userID: userID, searchFilter: searchFilter });
    };

    const fetchReferCommissionAndDiscountHistoryMutation = useFetchReferCommissionAndDiscountHistoryList({
        onSuccess: (response: any) => {
            if (response.statusCode === 200) {
                if (isNotNullUndefined(response.data) && response.data.length > 0) {
                    const formattedData = response.data.map((data: any) => ({
                        ...data,
                        commission: `$${(data.commission ?? 0).toFixed(2)}`,
                        discount: `$${(data.discount ?? 0).toFixed(2)}`,
                        statusName: [data.tripScheduleStatus]
                    }));
                    setReferCommisionAndDiscountHistoryList(formattedData);
                    const recordCount = formattedData[0]?.recordCount;
                    setTotalPages(Math.ceil(recordCount / pageSize));
                    setIsLoading(false);
                } else {
                    setReferCommisionAndDiscountHistoryList([]);
                    setIsLoading(false);
                }
            } else {
                message.error(response.statusMessage);
                setIsLoading(false);
            }
        },
        onError: (error: any) => {
            message.error(error.message);
            setIsLoading(false);
        }
    });

    return (
        <div>
            <div className="flex justify-between align-middle pb-5">
                <h2 className="text-2xl font-semibold text-black">Refer Commission And Discount History</h2>
                <div className="w-80 max-w-80">
                    <Search
                        placeholder="Search Commission Summary"
                        size="large"
                        enterButton
                        className="search-field"
                        onSearch={onSearch}
                    />
                </div>
            </div>

            <ConfigProvider
                theme={{
                    components: {
                        Tabs: {
                            titleFontSizeLG: 16,
                        },
                    }
                }}
            >
                <Tabs defaultActiveKey="0" onTabClick={tabClicked}>
                    {
                        tabStatus.map((x: any) => (
                            <TabPane tab={`${x.name}`} key={`${x.id}`}>
                                <Table dataSource={referCommisionAndDiscountHistoryList} className="userlist-tabs-table" bordered pagination={{ pageSize: pageSize, position: ['none'] }} locale={{
                                    emptyText: loading ? <Spin /> : (
                                        <div className="ant-empty">
                                            <div className="ant-empty-image">
                                                <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                                                    <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                                                        <ellipse fill="#f5f5f5" cx="32" cy="33" rx="32" ry="7"></ellipse>
                                                        <g fillRule="nonzero" stroke="#d9d9d9">
                                                            <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                                                            <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#fafafa"></path>
                                                        </g>
                                                    </g>
                                                </svg>
                                            </div>
                                            <div className="ant-empty-description">No data</div>
                                        </div>
                                    )
                                }}
                                >
                                    {
                                        activeTab === "commission" ? (
                                            <Column title="User Name" dataIndex="userName" key="userName" align="center" width={200} />
                                        ) : (
                                            <Column title="Referred User Name" dataIndex="referredUserName" key="referredUserName" align="center" width={200} />
                                        )
                                    }
                                    {
                                        activeTab === "commission" ? (
                                            <Column title="User Email" dataIndex="userEmail" key="userEmail" align="center" width={150} />
                                        ) : (
                                            <Column title="Referred User Email" dataIndex="referredUserEmail" key="referredUserEmail" width={200} />
                                        )
                                    }
                                    <Column title="Trip Name" dataIndex="tripName" key="tripName" align="center" width={200} />
                                    <Column title="Trip Schedule Status" width={200} dataIndex="tripScheduleStatus" key="tripScheduleStatus" align="center"
                                        render={(tripScheduleStatus: string) => (
                                            <Tag color={tripScheduleStatus === "Completed" ? "green" : tripScheduleStatus === "Pending" ? "orange" : tripScheduleStatus === "Upcoming" ? "blue" : tripScheduleStatus === "Running" ? "purple" : tripScheduleStatus === "Expired" ? "magenta" : "red"}>{tripScheduleStatus.toUpperCase()}</Tag>
                                        )}
                                    />
                                    {
                                        activeTab === "commission" ? (
                                            <Column title="Referred User Name" dataIndex="referredUserName" key="referredUserName" align="center" width={200} />
                                        ) : (
                                            <Column title="Referal User Name" dataIndex="userName" key="userName" align="center" width={150} />
                                        )
                                    }
                                    {
                                        activeTab === "commission" ? (
                                            <Column title="Referred User Email" dataIndex="referredUserEmail" key="referredUserEmail" width={200} />
                                        ) : (
                                            <Column title="Referal User Email" dataIndex="userEmail" key="userEmail" align="center" width={150} />
                                        )
                                    }
                                    {
                                        activeTab === "commission" ? (
                                            <Column title="Total Commission" dataIndex="commission" key="commission" align="center" width={150} />
                                        ) : (
                                            <Column title="Total Discount" dataIndex="discount" key="discount" align="center" width={150} />
                                        )
                                    }
                                </Table>
                                <div>
                                    <Pagination
                                        currentPage={pageNumber.current}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            </TabPane>
                        ))
                    }
                </Tabs>
            </ConfigProvider>
        </div>
    );
}

export default ReferCommisionAndDiscountHistory;
